import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Mail, Phone } from "lucide-react";

const STATUS = [
  { key: "new", label: "Neu", color: "bg-amber-100 text-amber-800" },
  { key: "read", label: "Gelesen", color: "bg-blue-100 text-blue-800" },
  { key: "replied", label: "Beantwortet", color: "bg-emerald-100 text-emerald-800" },
  { key: "closed", label: "Geschlossen", color: "bg-slate-200 text-slate-600" },
];

export default function AdminContacts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);

  const load = () => api.get("/admin/contacts").then(({ data }) => { setItems(data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const update = async (id, payload) => {
    try {
      await api.patch(`/admin/contacts/${id}`, payload);
      toast.success("Aktualisiert");
      load();
      if (sel?.id === id) setSel((s) => ({ ...s, ...payload }));
    } catch { toast.error("Fehler"); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-8" data-testid="admin-contacts">
      <h1 className="font-heading text-3xl font-bold text-[#1A2433] mb-2">Kontakt-Inbox</h1>
      <p className="text-slate-600 mb-6">{items.length} Nachrichten.</p>

      {loading ? <div className="text-slate-500">Lade…</div> : (
        <div className="space-y-3">
          {items.map((m) => {
            const opt = STATUS.find((s) => s.key === m.status) || STATUS[0];
            return (
              <button
                key={m.id}
                onClick={() => { setSel(m); if (m.status === "new") update(m.id, { status: "read" }); }}
                className="w-full text-left border border-slate-200 rounded-xl p-5 hover:border-slate-300 hover:shadow-sm transition-all"
                data-testid={`contact-row-${m.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="font-semibold text-[#1A2433]">{m.data.name}</div>
                    <div className="text-xs text-slate-500">{m.data.email} {m.data.phone && `· ${m.data.phone}`}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${opt.color}`}>{opt.label}</span>
                    <span className="text-xs text-slate-400">{new Date(m.created_at).toLocaleDateString("de-CH")}</span>
                  </div>
                </div>
                <div className="text-sm font-medium text-[#1A2433] mb-1">{m.data.subject}</div>
                <div className="text-sm text-slate-600 line-clamp-2">{m.data.message}</div>
              </button>
            );
          })}
          {items.length === 0 && <div className="text-slate-500 text-center py-10">Inbox ist leer.</div>}
        </div>
      )}

      {sel && (
        <div className="fixed inset-0 z-[10001] bg-black/40 flex items-stretch justify-end" onClick={() => setSel(null)}>
          <div className="bg-white w-full max-w-2xl h-full overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="mbw-overline mb-2">Kontaktanfrage</div>
                <h2 className="font-heading text-2xl font-bold text-[#1A2433]">{sel.data.subject}</h2>
                <div className="text-sm text-slate-500">{new Date(sel.created_at).toLocaleString("de-CH")}</div>
              </div>
              <button onClick={() => setSel(null)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>

            <div className="bg-slate-50 rounded-xl p-5 mb-6 space-y-2 text-sm">
              <div><span className="text-slate-500">Name:</span> <strong>{sel.data.name}</strong></div>
              <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /> <a href={`mailto:${sel.data.email}`} className="text-[#991B1B] hover:underline">{sel.data.email}</a></div>
              {sel.data.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /> <a href={`tel:${sel.data.phone}`} className="text-[#991B1B] hover:underline">{sel.data.phone}</a></div>}
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Nachricht</div>
              <p className="text-[#1A2433] whitespace-pre-wrap">{sel.data.message}</p>
            </div>

            <div className="mb-6">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Status</label>
              <div className="flex flex-wrap gap-2">
                {STATUS.map((s) => (
                  <button key={s.key} onClick={() => update(sel.id, { status: s.key })}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                      sel.status === s.key ? "bg-[#1A2433] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <a href={`mailto:${sel.data.email}?subject=Re: ${encodeURIComponent(sel.data.subject)}`} className="btn-primary">
              <Mail className="w-4 h-4" /> Antworten
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
